<template>
  <div>
    <!-- État de chargement -->
    <b-card
      v-if="isLoading"
      class="p-3"
      aria-busy="true"
      aria-live="polite"
    >
      <div class="d-flex justify-content-center">
        <b-spinner
          variant="primary"
          label="Chargement..."
          aria-label="Chargement des informations du livreur"
        />
      </div>
    </b-card>

    <!-- État d'erreur -->
    <b-alert
      v-else-if="error"
      show
      variant="danger"
      aria-live="assertive"
      aria-label="Erreur lors du chargement des informations du livreur"
    >
      <h4 class="alert-heading">
        Erreur
      </h4>
      <div class="alert-body">
        {{ error }}
      </div>
    </b-alert>

    <!-- Contenu chargé avec succès -->
    <div v-else>
      <!-- Carte d'en-tête avec informations enrichies -->
      <b-card
        class="mb-3"
        aria-label="Résumé du livreur"
      >
        <b-row class="align-items-start">
          <b-col
            lg="8"
            md="7"
            sm="12"
          >
            <div class="d-flex align-items-center">
              <b-avatar
                :src="deliveryMan.avatar || null"
                :text="getInitials(deliveryMan.username)"
                size="4rem"
                variant="light-primary"
                class="mr-3"
                :aria-label="`Avatar du livreur ${deliveryMan.username}`"
              >
                <feather-icon
                  v-if="!deliveryMan.avatar"
                  icon="UserIcon"
                  size="28"
                  aria-hidden="true"
                />
              </b-avatar>

              <div>
                <h2
                  class="mb-1"
                  aria-label="Nom du livreur"
                >
                  {{ deliveryMan.username }}
                </h2>
                <div class="d-flex align-items-center mb-2">
                  <b-badge
                    :variant="deliveryMan.is_active ? 'danger' : 'success'"
                    pill
                    class="mr-2"
                  >
                    {{ deliveryMan.is_active ? 'Actif' : 'Inactif' }}
                  </b-badge>
                </div>

                <!-- Quick Info -->
                <div class="d-flex flex-wrap">
                  <div class="mr-3 mb-1">
                    <feather-icon
                      icon="PhoneIcon"
                      size="14"
                      class="text-primary mr-1"
                    />
                    <small>{{ deliveryMan.phone || 'N/A' }}</small>
                  </div>
                  <div class="mr-3 mb-1">
                    <feather-icon
                      icon="PhoneIcon"
                      size="14"
                      class="text-warning mr-1"
                    />
                    <small>{{ deliveryMan.phone2 || 'N/A' }}</small>
                  </div>
                  <div class="mr-3 mb-1">
                    <feather-icon
                      icon="MailIcon"
                      size="14"
                      class="text-primary mr-1"
                    />
                    <small>{{ deliveryMan.email || 'N/A' }}</small>
                  </div>
                  <div class="mr-3 mb-1">
                    <feather-icon
                      icon="HomeIcon"
                      size="14"
                      class="text-primary mr-1"
                    />
                    <small>{{ deliveryMan.repository_name || 'N/A' }}</small>
                  </div>
                </div>
              </div>
            </div>
          </b-col>

          <b-col
            lg="4"
            md="5"
            sm="12"
            class="text-md-right text-left mt-3 mt-md-0"
          >
            <!-- Action buttons -->
            <div class="action-buttons-container mb-2">
              <div class="action-buttons-row">
                <b-button
                  v-if="userIsAdmin"
                  :variant="deliveryMan.is_active ? 'outline-warning' : 'outline-success'"
                  size="sm"
                  class="action-btn toggle-btn mr-2 mb-2"
                  @click="toggleDeliveryManStatus"
                >
                  <feather-icon
                    :icon="deliveryMan.is_active ? 'UserXIcon' : 'UserCheckIcon'"
                    size="16"
                    class="mr-50"
                  />
                  <span>{{ deliveryMan.is_active ? 'Désactiver' : 'Activer' }}</span>
                </b-button>

                <b-button
                  v-if="userIsAdmin"
                  variant="outline-primary"
                  size="sm"
                  class="action-btn edit-btn mr-2 mb-2"
                  :to="{ name: 'delivery-man-edit', params: { id: deliveryMan.id }}"
                >
                  <feather-icon
                    icon="EditIcon"
                    size="16"
                    class="mr-50"
                  />
                  <span>Modifier</span>
                </b-button>

                <b-button
                  v-if="userIsAdmin"
                  variant="outline-secondary"
                  size="sm"
                  class="action-btn password-btn mr-2 mb-2"
                  @click="openPasswordModal"
                >
                  <feather-icon
                    icon="LockIcon"
                    size="16"
                    class="mr-50"
                  />
                  <span>Mot de passe</span>
                </b-button>

                <span
                  v-if="userIsAdmin"
                  v-b-tooltip.hover
                  :title="deliveryMan && deliveryMan.can_be_deleted !== false ? 'Supprimer' : (deliveryMan && deliveryMan.deletion_block_reason || 'Impossible de supprimer ce livreur')"
                  class="d-inline-block mr-2 mb-2"
                >
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    class="action-btn delete-btn"
                    :disabled="deliveryMan && deliveryMan.can_be_deleted === false"
                    :class="{ 'cursor-not-allowed not-allowed': deliveryMan && deliveryMan.can_be_deleted === false }"
                    @click="confirmSafeDelete"
                  >
                    <feather-icon
                      icon="Trash2Icon"
                      size="16"
                      class="mr-50"
                    />
                    <span>Supprimer</span>
                  </b-button>
                </span>

                <b-button
                  variant="outline-info"
                  size="sm"
                  class="action-btn back-btn mr-2 mb-2"
                  :to="{ name: 'delivery-man-list' }"
                >
                  <feather-icon
                    icon="ArrowLeftIcon"
                    size="16"
                    class="mr-50"
                  />
                  <span>Retour</span>
                </b-button>
              </div>
            </div>

            <!-- Stats Summary -->
            <div class="text-md-right text-left">
              <small class="text-muted">Membre depuis {{ formatDate(deliveryMan.created_at) }}</small>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <!-- Quick Stats Cards -->
      <b-row class="mb-3">
        <b-col
          lg="3"
          md="6"
          sm="12"
        >
          <b-card class="text-center stats-mini-card">
            <div class="avatar bg-light-success mx-auto mb-1">
              <feather-icon
                icon="PackageIcon"
                size="20"
              />
            </div>
            <h4 class="mb-0">
              {{ deliveryStats.totalDeliveries || 0 }}
            </h4>
            <small class="text-muted">Livraisons</small>
          </b-card>
        </b-col>

        <b-col
          lg="3"
          md="6"
          sm="12"
        >
          <b-card class="text-center stats-mini-card">
            <div class="avatar bg-light-info mx-auto mb-1">
              <feather-icon
                icon="CheckCircleIcon"
                size="20"
              />
            </div>
            <h4 class="mb-0">
              {{ deliveryStats.successRate || 0 }}%
            </h4>
            <small class="text-muted">Taux de réussite</small>
          </b-card>
        </b-col>

        <b-col
          lg="3"
          md="6"
          sm="12"
        >
          <b-card class="text-center stats-mini-card">
            <div class="avatar bg-light-warning mx-auto mb-1">
              <feather-icon
                icon="ClockIcon"
                size="20"
              />
            </div>
            <h4 class="mb-0">
              {{ deliveryStats.avgDeliveryTime || 0 }}h
            </h4>
            <small class="text-muted">Temps moyen</small>
          </b-card>
        </b-col>

        <b-col
          lg="3"
          md="6"
          sm="12"
        >
          <b-card class="text-center stats-mini-card">
            <div class="avatar bg-light-primary mx-auto mb-1">
              <feather-icon
                icon="StarIcon"
                size="20"
              />
            </div>
            <h4 class="mb-0">
              {{ deliveryStats.rating || 0 }}/5
            </h4>
            <small class="text-muted">Évaluation</small>
          </b-card>
        </b-col>
      </b-row>

      <b-card>
        <b-tabs nav-class="nav-tabs-modern">
          <b-tab active>
            <template #title>
              <feather-icon
                icon="UserIcon"
                size="16"
              />
              <span class="ml-1">Information</span>
            </template>

            <ShowInfo :delivery-man="deliveryMan" />

            <!-- Account Security & Audit Card -->
            <b-card
              title="Sécurité du compte & Audit"
              class="mt-4 shadow-sm"
              aria-label="Sécurité du compte et historique des modifications"
            >
              <div class="d-flex justify-content-between align-items-center flex-wrap" style="gap: 1rem;">
                <div
                  v-if="deliveryMan.last_password_change"
                  class="d-flex align-items-center"
                >
                  <div class="avatar bg-light-primary p-2 mr-2">
                    <feather-icon
                      icon="LockIcon"
                      size="20"
                      class="text-primary"
                    />
                  </div>
                  <div>
                    <strong class="d-block text-dark font-weight-bold">
                      Dernière modification du mot de passe
                    </strong>
                    <span class="small text-muted">
                      Modifié le {{ formatDateTime(deliveryMan.last_password_change.created_at) }} par <strong>{{ deliveryMan.last_password_change.changed_by_display }}</strong>
                    </span>
                  </div>
                </div>
                <div
                  v-else
                  class="text-muted small d-flex align-items-center"
                >
                  <feather-icon
                    icon="ShieldIcon"
                    class="mr-50 text-secondary"
                    size="18"
                  />
                  <span>Aucune modification de mot de passe enregistrée pour ce compte.</span>
                </div>

                <b-button
                  v-if="userIsAdmin"
                  variant="outline-primary"
                  size="sm"
                  @click="openPasswordModal"
                >
                  <feather-icon
                    icon="LockIcon"
                    size="14"
                    class="mr-50"
                  />
                  <span>Changer le mot de passe</span>
                </b-button>
              </div>

              <!-- Collapsible Audit History List -->
              <div
                v-if="deliveryMan.password_change_history && deliveryMan.password_change_history.length > 0"
                class="mt-3 pt-2 border-top"
              >
                <b-button
                  v-b-toggle.password-history-collapse
                  variant="flat-primary"
                  size="sm"
                  class="p-0 font-weight-bold"
                >
                  <feather-icon
                    icon="HistoryIcon"
                    size="14"
                    class="mr-25"
                  />
                  Voir l'historique complet ({{ deliveryMan.password_change_history.length }})
                </b-button>

                <b-collapse
                  id="password-history-collapse"
                  class="mt-2"
                >
                  <b-list-group
                    flush
                    class="border rounded"
                  >
                    <b-list-group-item
                      v-for="log in deliveryMan.password_change_history"
                      :key="log.id"
                      class="py-50 px-1 d-flex justify-content-between align-items-center small"
                    >
                      <span>
                        <feather-icon
                          icon="UserCheckIcon"
                          size="12"
                          class="mr-50 text-primary"
                        />
                        <strong>{{ log.changed_by_display }}</strong>
                      </span>
                      <span class="text-muted">{{ formatDateTime(log.created_at) }}</span>
                    </b-list-group-item>
                  </b-list-group>
                </b-collapse>
              </div>

              <!-- Account audit metadata (create / update timestamps) -->
              <div class="mt-3 pt-2 border-top">
                <b-list-group flush class="mb-0">
                  <b-list-group-item class="py-50 px-0 d-flex justify-content-between align-items-center small">
                    <span>
                      <feather-icon
                        icon="PlusCircleIcon"
                        size="14"
                        class="mr-50 text-success"
                      />
                      <span class="text-muted">Compte créé le</span>
                    </span>
                    <span class="text-muted">{{ formatDateTime(deliveryMan.created_at) }}</span>
                  </b-list-group-item>
                  <b-list-group-item class="py-50 px-0 d-flex justify-content-between align-items-center small">
                    <span>
                      <feather-icon
                        icon="RefreshCwIcon"
                        size="14"
                        class="mr-50 text-primary"
                      />
                      <span class="text-muted">Dernière mise à jour</span>
                    </span>
                    <span class="text-muted">{{ formatDateTime(deliveryMan.updated_at) }}</span>
                  </b-list-group-item>
                </b-list-group>
              </div>
            </b-card>
          </b-tab>

          <b-tab>
            <template #title>
              <feather-icon
                icon="TruckIcon"
                size="16"
              />
              <span class="ml-1">Livraisons</span>
            </template>

            <b-card-text>
              <div class="text-center py-4">
                <feather-icon
                  icon="TruckIcon"
                  size="48"
                  class="text-muted mb-2"
                />
                <h5 class="text-muted">
                  Historique des livraisons
                </h5>
                <p class="text-muted">
                  Cette section affichera l'historique des livraisons du livreur
                </p>
                <!-- TODO: Add deliveries component here -->
              </div>
            </b-card-text>
          </b-tab>

          <b-tab>
            <template #title>
              <feather-icon
                icon="DollarSignIcon"
                size="16"
              />
              <span class="ml-1">Paiements</span>
            </template>

            <b-card-text>
              <div class="text-center py-4">
                <feather-icon
                  icon="DollarSignIcon"
                  size="48"
                  class="text-muted mb-2"
                />
                <h5 class="text-muted">
                  Historique des paiements
                </h5>
                <p class="text-muted">
                  Cette section affichera l'historique des paiements du livreur
                </p>
                <!-- TODO: Add payments component here -->
              </div>
            </b-card-text>
          </b-tab>

          <b-tab>
            <template #title>
              <feather-icon
                icon="BarChart3Icon"
                size="16"
              />
              <span class="ml-1">Statistiques</span>
            </template>

            <b-card-text>
              <div class="text-center py-4">
                <feather-icon
                  icon="BarChart3Icon"
                  size="48"
                  class="text-muted mb-2"
                />
                <h5 class="text-muted">
                  Statistiques détaillées
                </h5>
                <p class="text-muted">
                  Cette section affichera des graphiques et statistiques détaillées
                </p>
                <!-- TODO: Add charts and detailed statistics here -->
              </div>
            </b-card-text>
          </b-tab>
        </b-tabs>
      </b-card>
    </div>

    <!-- Status toggle confirmation modal -->
    <b-modal
      ref="statusModal"
      :title="deliveryMan.is_active ? 'Désactiver le livreur' : 'Activer le livreur'"
      @ok="handleStatusConfirmed"
    >
      <p>Êtes-vous sûr de vouloir {{ deliveryMan.is_active ? 'désactiver' : 'activer' }} ce livreur?</p>
    </b-modal>

    <!-- Password Change Modal -->
    <b-modal
      id="deliveryman-password-modal"
      v-model="showPasswordModal"
      centered
      hide-footer
      size="lg"
      title-tag="div"
      @hidden="resetPasswordForm"
    >
      <template #modal-header="{ close }">
        <div class="w-100 d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <div class="avatar bg-light-primary p-2 mr-2">
              <feather-icon
                icon="LockIcon"
                size="20"
                class="text-primary"
              />
            </div>
            <div>
              <h5 class="mb-0">
                Changer le mot de passe
              </h5>
              <small
                v-if="deliveryMan"
                class="text-muted"
              >
                {{ deliveryMan.username }} • {{ deliveryMan.email }}
              </small>
            </div>
          </div>
          <b-button
            variant="flat-secondary"
            size="sm"
            class="btn-icon"
            @click="close"
          >
            <feather-icon icon="XIcon" />
          </b-button>
        </div>
      </template>

      <div v-if="deliveryMan">
        <!-- Password Audit Log Banner -->
        <b-alert
          v-if="deliveryMan.last_password_change"
          show
          variant="light-primary"
          class="mb-3 border-left-primary shadow-sm"
        >
          <div class="d-flex align-items-center">
            <feather-icon
              icon="ClockIcon"
              size="20"
              class="mr-2 text-primary"
            />
            <div>
              <strong class="d-block text-dark font-weight-bold">
                Dernière modification du mot de passe
              </strong>
              <span class="small text-muted">
                Modifié le {{ formatDateTime(deliveryMan.last_password_change.created_at) }} par <strong>{{ deliveryMan.last_password_change.changed_by_display }}</strong>
              </span>
            </div>
          </div>
        </b-alert>

        <!-- Collapsible Audit History -->
        <div
          v-if="deliveryMan.password_change_history && deliveryMan.password_change_history.length > 0"
          class="mb-3"
        >
          <b-button
            v-b-toggle.modal-password-history-collapse
            variant="flat-primary"
            size="sm"
            class="p-0 font-weight-bold"
          >
            <feather-icon
              icon="HistoryIcon"
              size="14"
              class="mr-25"
            />
            Historique des modifications ({{ deliveryMan.password_change_history.length }})
          </b-button>

          <b-collapse
            id="modal-password-history-collapse"
            class="mt-2"
          >
            <b-list-group
              flush
              class="border rounded"
            >
              <b-list-group-item
                v-for="log in deliveryMan.password_change_history"
                :key="log.id"
                class="py-50 px-1 d-flex justify-content-between align-items-center small"
              >
                <span>
                  <feather-icon
                    icon="UserCheckIcon"
                    size="12"
                    class="mr-50 text-primary"
                  />
                  <strong>{{ log.changed_by_display }}</strong>
                </span>
                <span class="text-muted">{{ formatDateTime(log.created_at) }}</span>
              </b-list-group-item>
            </b-list-group>
          </b-collapse>
        </div>

        <b-form @submit.prevent="savePasswordChange">
          <!-- New Password -->
          <b-form-group label-class="form-label-premium"
            label="Nouveau mot de passe *"
            label-for="modal-password"
            class="mb-3"
          >
            <b-input-group class="input-group-merge">
              <b-form-input class="premium-input"
                id="modal-password"
                v-model="passwordForm.password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Entrez le nouveau mot de passe"
                required
              />
              <b-input-group-append is-text>
                <feather-icon
                  :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'"
                  class="cursor-pointer"
                  @click="showPassword = !showPassword"
                />
              </b-input-group-append>
            </b-input-group>

            <!-- Password Strength Meter -->
            <div
              v-if="passwordForm.password"
              class="mt-2"
            >
              <div class="d-flex justify-content-between align-items-center mb-50">
                <small class="text-muted">Force du mot de passe:</small>
                <small
                  :class="'text-' + passwordStrengthClass"
                  class="font-weight-bold"
                >
                  {{ passwordStrengthText }}
                </small>
              </div>
              <b-progress
                :value="passwordStrength"
                :variant="passwordStrengthClass"
                height="6px"
              />
            </div>
          </b-form-group>

          <!-- Confirm Password -->
          <b-form-group label-class="form-label-premium"
            label="Confirmer le mot de passe *"
            label-for="modal-confirm-password"
            class="mb-3"
          >
            <b-input-group class="input-group-merge">
              <b-form-input class="premium-input"
                id="modal-confirm-password"
                v-model="passwordForm.confirm_password"
                :type="showConfirmPassword ? 'text' : 'password'"
                placeholder="Confirmez le mot de passe"
                required
              />
              <b-input-group-append is-text>
                <feather-icon
                  :icon="showConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                  class="cursor-pointer"
                  @click="showConfirmPassword = !showConfirmPassword"
                />
              </b-input-group-append>
            </b-input-group>

            <small
              v-if="passwordForm.confirm_password && !passwordsMatch"
              class="text-danger mt-50 d-block"
            >
              Les mots de passe ne correspondent pas.
            </small>
          </b-form-group>

          <!-- Generate Password Button -->
          <!-- Generate Password Button -->
          <div class="mb-4">
            <PasswordGenerator
              :user="deliveryMan"
              user-type="deliveryman"
              button-text="Générer un mot de passe sécurisé"
              @generated="onPasswordGenerated"
            />
          </div>

          <!-- Action Footer -->
          <div class="d-flex justify-content-end">
            <b-button
              variant="outline-secondary"
              class="mr-2"
              @click="showPasswordModal = false"
            >
              Annuler
            </b-button>
            <b-button
              variant="primary"
              :disabled="isPasswordSubmitting || !isPasswordFormValid"
              type="submit"
            >
              <b-spinner
                v-if="isPasswordSubmitting"
                small
                class="mr-50"
              />
              <feather-icon
                v-else
                icon="SaveIcon"
                size="16"
                class="mr-50"
              />
              Enregistrer le mot de passe
            </b-button>
          </div>
        </b-form>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BTabs, BTab, BCardText, BCard, BButton, BSpinner,
  BAlert, BModal, BRow, BCol, BBadge, BAvatar,
  BListGroup, BListGroupItem, BCollapse, VBToggle,
  BForm, BFormGroup, BInputGroup, BInputGroupAppend, BFormInput, BProgress,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import PasswordGenerator from '@core/components/password/PasswordGenerator.vue'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import storeAuth from '@/store/store'
import ShowInfo from './ShowInfo.vue'

export default {
  components: {
    PasswordGenerator,
    BTabs,
    BTab,
    BCardText,
    BCard,
    BButton,
    BSpinner,
    BAlert,
    BModal,
    BRow,
    BCol,
    BBadge,
    BAvatar,
    BListGroup,
    BListGroupItem,
    BCollapse,
    BForm,
    BFormGroup,
    BInputGroup,
    BInputGroupAppend,
    BFormInput,
    BProgress,
    ShowInfo,
  },

  directives: {
    'b-toggle': VBToggle,
  },

  data() {
    return {
      deliveryMan: {},
      isLoading: true,
      error: null,
      // Password modal state
      showPasswordModal: false,
      isPasswordSubmitting: false,
      passwordForm: {
        password: '',
        confirm_password: '',
      },
      showPassword: false,
      showConfirmPassword: false,
      // Stats
      deliveryStats: {
        totalDeliveries: 0,
        successRate: 0,
        avgDeliveryTime: 0,
        rating: 0,
      },
    }
  },

  computed: {
    storeAuth() {
      return storeAuth
    },

    userIsAdmin() {
      const { user } = storeAuth.state
      if (!user) return false

      // Check if user has admin role (multiple ways it could be stored)
      return user.role === 'admin'
             || user.role === 'Admin'
             || (user.groups && user.groups.some(group => group.name === 'admin' || group.name === 'Admin'))
             || (user.groups && Array.isArray(user.groups) && user.groups.includes('admin'))
             || user.is_admin === true
             || user.is_superuser === true
    },

    passwordStrength() {
      let strength = 0
      const { password } = this.passwordForm

      if (!password) return 0

      if (password.length >= 8) strength += 25
      if (/[A-Z]/.test(password)) strength += 25
      if (/[0-9]/.test(password)) strength += 25
      if (/[^A-Za-z0-9]/.test(password)) strength += 25

      return strength
    },

    passwordStrengthClass() {
      if (this.passwordStrength <= 25) return 'danger'
      if (this.passwordStrength <= 50) return 'warning'
      if (this.passwordStrength <= 75) return 'info'
      return 'success'
    },

    passwordStrengthText() {
      if (this.passwordStrength <= 25) return 'Faible'
      if (this.passwordStrength <= 50) return 'Moyen'
      if (this.passwordStrength <= 75) return 'Bon'
      return 'Fort'
    },

    passwordsMatch() {
      return (
        this.passwordForm.password
        && this.passwordForm.confirm_password
        && this.passwordForm.password === this.passwordForm.confirm_password
      )
    },

    isPasswordFormValid() {
      return (
        this.passwordForm.password
        && this.passwordForm.confirm_password
        && this.passwordsMatch
        && this.passwordForm.password.length >= 8
      )
    },
  },

  created() {
    this.fetchDeliveryManDetails()
  },

  methods: {
    async fetchDeliveryManDetails() {
      this.isLoading = true
      try {
        // Use the new dedicated endpoint
        const response = await axios.get(`/api/auth/users/delivery-men/${this.$route.params.id}/`)
        this.deliveryMan = response.data

        // Load delivery statistics (mock data for now)
        this.loadDeliveryStats()
      } catch (error) {
        this.error = error.response?.data?.message || 'Une erreur est survenue lors du chargement'
      } finally {
        this.isLoading = false
      }
    },

    async confirmSafeDelete() {
      const result = await this.$swal({
        title: 'Supprimer le livreur ?',
        text: `Êtes-vous sûr de vouloir supprimer ${this.deliveryMan.username || 'ce livreur'} ?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Oui, supprimer',
        cancelButtonText: 'Annuler',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })

      if (result.value) {
        try {
          this.isLoading = true
          await axios.delete(`/api/auth/users/delivery-men/${this.deliveryMan.id}/`)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Succès',
              icon: 'CheckCircleIcon',
              text: 'Le livreur a été supprimé avec succès',
              variant: 'success',
            },
          })
          this.$router.push({ name: 'delivery-man-list' })
        } catch (error) {
          const errorMsg = error.response?.data?.error || error.response?.data?.message || error.response?.data?.detail || error.message
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Erreur',
              icon: 'AlertCircleIcon',
              text: errorMsg,
              variant: 'danger',
            },
          })
        } finally {
          this.isLoading = false
        }
      }
    },

    loadDeliveryStats() {
      // TODO: Replace with actual API call
      // For now, we'll use mock data based on user ID to simulate different stats
      const userId = this.deliveryMan.id || 1
      this.deliveryStats = {
        totalDeliveries: Math.floor(Math.random() * 100) + 20 + (userId % 10),
        successRate: Math.floor(Math.random() * 20) + 80, // 80-100%
        avgDeliveryTime: (Math.random() * 3 + 1).toFixed(1), // 1-4 hours
        rating: (Math.random() * 1.5 + 3.5).toFixed(1), // 3.5-5.0
      }
    },

    getInitials(name) {
      if (!name) return '??'
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .substring(0, 2)
    },

    formatDate(dateString) {
      return format(new Date(dateString), 'dd MMMM yyyy, HH:mm', { locale: fr })
    },

    toggleDeliveryManStatus() {
      this.$refs.statusModal.show()
    },

    async handleStatusConfirmed() {
      try {
        // Use the new dedicated endpoint for toggling activation
        const response = await axios.post(`/api/auth/users/delivery-men/toggle-activation/${this.deliveryMan.id}/`)
        this.deliveryMan = response.data
        this.showToast('success', `Le livreur a été ${this.deliveryMan.is_active ? 'activé' : 'désactivé'} avec succès`)
      } catch (error) {
        this.showToast('danger', error.response?.data?.error || 'Erreur lors du changement de statut')
      }
    },

    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: variant === 'success' ? 'Succès' : 'Erreur',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon',
          variant,
          text,
        },
      })
    },

    formatDateTime(dateString) {
      if (!dateString) return 'N/A'
      try {
        return format(new Date(dateString), 'dd MMM yyyy à HH:mm', { locale: fr })
      } catch (e) {
        return dateString
      }
    },

    openPasswordModal() {
      this.showPasswordModal = true
    },

    resetPasswordForm() {
      this.passwordForm = {
        password: '',
        confirm_password: '',
      }
      this.showPassword = false
      this.showConfirmPassword = false
      this.isPasswordSubmitting = false
    },

    onPasswordGenerated(generatedPassword) {
      this.passwordForm.password = generatedPassword
      this.passwordForm.confirm_password = generatedPassword
      this.showPassword = true
      this.showConfirmPassword = true
    },

    async savePasswordChange() {
      if (!this.isPasswordFormValid) return

      this.isPasswordSubmitting = true
      try {
        await axios.post(
          `/api/auth/users/delivery-men/change-password/${this.deliveryMan.id}/`,
          this.passwordForm,
        )

        this.showToast('success', 'Mot de passe modifié avec succès')
        this.showPasswordModal = false
        this.resetPasswordForm()
        // Refresh delivery man data to update password history
        await this.fetchDeliveryManDetails()
      } catch (error) {
        const errorMsg = error.response?.data?.error
          || error.response?.data?.detail
          || 'Erreur lors du changement de mot de passe'
        this.showToast('danger', errorMsg)
      } finally {
        this.isPasswordSubmitting = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.info-section {
  margin-bottom: 1.5rem;
}

.section-title {
  margin-bottom: 1rem;
  font-weight: 500;
  color: #5e5873;
  font-size: 1.1rem;
}

.delivery-man-info-container {
  background-color: #f8f8f8;
  border-radius: 0.5rem;
  padding: 1.5rem;
}

/* Enhanced Action Buttons Styling */
.action-buttons-container {
  .action-buttons-row {
    gap: 0.5rem;
    flex-wrap: wrap;

    .action-btn:not(:last-child) {
      margin-right: 0.5rem;
      margin-bottom: 0.5rem;
    }

    .action-btn:last-child {
      margin-bottom: 0.5rem;
    }
  }

  .action-btn {
    min-width: auto;
    padding: 0.375rem 0.75rem;
    border-radius: 0.375rem;
    font-weight: 500;
    font-size: 0.875rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    border-width: 1px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent);
      transition: left 0.4s;
    }

    &:hover::before {
      left: 100%;
    }

    .btn-icon {
      transition: transform 0.2s ease;
      margin-right: 0.375rem;
    }

    .btn-text {
      font-size: 0.875rem;
      font-weight: 500;
    }

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

      .btn-icon {
        transform: scale(1.1);
      }
    }

    &.toggle-btn {
      &.btn-outline-success {
        border-color: #28c76f;
        color: #28c76f;

        &:hover {
          background: #28c76f;
          border-color: #28c76f;
          color: white;
          box-shadow: 0 6px 20px rgba(40, 199, 111, 0.3);
        }
      }

      &.btn-outline-warning {
        border-color: #0d4399;
        color: #0d4399;

        &:hover {
          background: #0d4399;
          border-color: #0d4399;
          color: white;
          box-shadow: 0 6px 20px rgba(255, 159, 67, 0.3);
        }
      }
    }

    &.edit-btn {
      border-color: #0A2634;
      color: #0A2634;

      &:hover {
        background: linear-gradient(45deg, #0A2634, #ff4d4d);
        border-color: #0A2634;
        color: white;
        box-shadow: 0 6px 20px rgba(10, 38, 52, 0.3);
      }
    }

    &.password-btn {
      border-color: #1D1D1B;
      color: #1D1D1B;

      &:hover {
        background: #1D1D1B;
        border-color: #1D1D1B;
        color: white;
        box-shadow: 0 6px 20px rgba(29, 29, 27, 0.3);
      }
    }

    &.back-btn {
      border-color: #00cfe8;
      color: #00cfe8;

      &:hover {
        background: #00cfe8;
        border-color: #00cfe8;
        color: white;
        box-shadow: 0 6px 20px rgba(0, 207, 232, 0.3);
      }
    }
  }
}

/* Enhanced Stats Cards for Show Page */
.stats-mini-card {
  border: 1px solid #e9ecef;
  border-radius: 0.75rem;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #0A2634, #ff4d4d);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(10, 38, 52, 0.1);
    border-color: #0A2634;

    &::before {
      opacity: 1;
    }

    .avatar {
      transform: scale(1.1);
    }
  }

  .avatar {
    width: 3rem;
    height: 3rem;
    border-radius: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

    &.bg-light-success {
      background: linear-gradient(45deg, rgba(40, 199, 111, 0.1), rgba(40, 199, 111, 0.2));
      color: #28c76f;
    }

    &.bg-light-info {
      background: linear-gradient(45deg, rgba(0, 207, 232, 0.1), rgba(0, 207, 232, 0.2));
      color: #00cfe8;
    }

    &.bg-light-warning {
      background: linear-gradient(45deg, rgba(255, 159, 67, 0.1), rgba(255, 159, 67, 0.2));
      color: #0d4399;
    }

    &.bg-light-primary {
      background: linear-gradient(45deg, rgba(10, 38, 52, 0.1), rgba(10, 38, 52, 0.2));
      color: #0A2634;
    }
  }

  h4 {
    font-weight: 700;
    color: #1D1D1B;
    margin-bottom: 0.25rem;
  }

  small {
    color: #6c757d;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

/* Enhanced Header Card */
.card {
  border-radius: 0.75rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
}

/* Modern Tab Styling */
.nav-tabs-modern {
  border-bottom: 2px solid #e9ecef;

  .nav-link {
    border: none;
    border-radius: 0;
    color: #6c757d;
    font-weight: 500;
    padding: 1rem 1.5rem;
    transition: all 0.3s ease;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      right: 0;
      height: 2px;
      background: #0A2634;
      transform: scaleX(0);
      transition: transform 0.3s ease;
    }

    &:hover {
      color: #0A2634;
      background: rgba(10, 38, 52, 0.05);
    }

    &.active {
      color: #0A2634;
      background: rgba(10, 38, 52, 0.05);

      &::after {
        transform: scaleX(1);
      }
    }
  }
}

/* Responsive Improvements for Show Page */
@media (max-width: 768px) {
  .action-buttons-container {
    .action-buttons-row {
      gap: 0.25rem;

      .action-btn:not(:last-child) {
        margin-right: 0.25rem;
      }
    }

    .action-btn {
      min-width: auto;
      flex: 1;
      margin-bottom: 0.5rem;

      .btn-text {
        display: none;
      }

      .btn-icon {
        margin-right: 0;
      }
    }
  }

  .stats-mini-card {
    margin-bottom: 1rem;

    .avatar {
      width: 2.5rem;
      height: 2.5rem;
    }

    h4 {
      font-size: 1.25rem;
    }
  }
}

@media (max-width: 576px) {
  .action-buttons-container {
    .action-buttons-row {
      flex-direction: column;

      .action-btn:not(:last-child) {
        margin-right: 0;
      }
    }

    .action-btn {
      width: 100%;
      justify-content: center;
      margin-bottom: 0.5rem;

      .btn-text {
        display: inline;
      }

      .btn-icon {
        margin-right: 0.5rem;
      }
    }
  }
}
</style>
